import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { SnackbarKey, useSnackbar } from 'notistack';

export type NetworkStatusContextType = {
  isOnline: boolean;
};

export const NetworkStatusContext = createContext<NetworkStatusContextType>({
  isOnline: true,
});

export const useNetworkStatus = () => useContext(NetworkStatusContext);

export const NetworkStatusProvider = ({ children }: React.PropsWithChildren = {}) => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const snackBarKey = useRef<SnackbarKey>('');

  useEffect(() => {
    // Update network status
    const handleStatusChange = () => {
      setIsOnline(navigator.onLine);
      if (navigator.onLine) {
        closeSnackbar(snackBarKey.current);
        snackBarKey.current = '';
      } else {
        snackBarKey.current = enqueueSnackbar("You're offline - some features are unavailable", { variant: 'warning', persist: true });
      }
    };

    // Listen to the online status
    window.addEventListener('online', handleStatusChange);

    // Listen to the offline status
    window.addEventListener('offline', handleStatusChange);

    // Specify how to clean up after this effect for performance improvment
    return () => {
      window.removeEventListener('online', handleStatusChange);
      window.removeEventListener('offline', handleStatusChange);
    };
  }, [isOnline]);

  const value = React.useMemo(() => ({ isOnline }), [isOnline]);

  return <NetworkStatusContext.Provider value={value}>{children}</NetworkStatusContext.Provider>;
};
